/**
 * GlobalSearch — Barre de recherche multi-scope dans le header
 */

import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";

interface SearchResult {
  id: string;
  type: "mission" | "project" | "crew" | "log" | "setting";
  title: string;
  subtitle: string;
  icon: string;
}

const TYPE_META: Record<string, { icon: string; label: string }> = {
  mission: { icon: "🛰", label: "Mission" },
  project: { icon: "📦", label: "Projet" },
  crew: { icon: "👤", label: "Agent" },
  log: { icon: "📡", label: "Log" },
  setting: { icon: "⚙", label: "Setting" },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        // CMD-03: recherche serveur unique (scopes log/setting désormais réels).
        const hits = await api.search(query, scope, 20);
        setResults(
          hits.map((h) => ({
            id: h.id,
            type: h.type,
            title: h.title,
            subtitle: h.subtitle,
            icon: TYPE_META[h.type]?.icon ?? "📌",
          })),
        );
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, scope]);

  const scopes = [
    { key: "all", label: "Tout" },
    { key: "mission", label: "🛰 Missions" },
    { key: "project", label: "📦 Projets" },
    { key: "crew", label: "👤 Équipage" },
    { key: "log", label: "📡 Logs" },
    { key: "setting", label: "⚙ Settings" },
  ];

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, maxWidth: "400px" }}>
      <input
        type="text"
        placeholder="🔍 Rechercher…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{
          width: "100%",
          background: "rgba(0,0,0,0.2)",
          border: "1px solid var(--line)",
          borderRadius: "12px",
          padding: "8px 14px",
          color: "var(--text)",
          fontSize: "13px",
          outline: "none",
          fontFamily: "var(--sans)",
        }}
      />

      {open && query.trim().length >= 2 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          marginTop: "8px",
          background: "rgba(18,22,41,0.98)",
          border: "1px solid var(--line)",
          borderRadius: "18px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          zIndex: 1000,
          overflow: "hidden",
        }}>
          {/* Scope tabs */}
          <div style={{ display: "flex", gap: "4px", padding: "8px", borderBottom: "1px solid var(--line)" }}>
            {scopes.map((s) => (
              <span
                key={s.key}
                className={`view-tab ${scope === s.key ? "active" : ""}`}
                style={{ fontSize: "11px", cursor: "pointer", padding: "5px 10px", borderRadius: "8px" }}
                onClick={() => setScope(s.key)}
              >
                {s.label}
              </span>
            ))}
          </div>

          {/* Results */}
          <div style={{ maxHeight: "400px", overflow: "auto", padding: "6px" }}>
            {loading && <div style={{ padding: "20px", textAlign: "center", color: "var(--muted)", fontSize: "13px" }}>Recherche…</div>}
            {!loading && results.length === 0 && (
              <div style={{ padding: "20px", textAlign: "center", color: "var(--muted)", fontSize: "13px" }}>
                Aucun résultat pour "{query}"
              </div>
            )}
            {results.map((r) => (
              <div
                key={`${r.type}-${r.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "12px",
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.02)",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(183,148,246,0.06)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.02)")}
                onClick={() => {
                  if (r.type === "mission") window.location.hash = `#/mission/${r.id}`;
                  setOpen(false);
                }}
              >
                <span style={{ fontSize: "16px" }}>{r.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", fontWeight: 500 }}>{r.title}</div>
                  {r.subtitle && (
                    <div style={{ fontSize: "11px", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.subtitle}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)", textTransform: "uppercase" }}>
                  {TYPE_META[r.type]?.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
